import { FieldValues } from 'react-hook-form';
import OptionsDrawerHeader from './Header';
import { OptionsDrawerProps } from './props';
import styles from './styles.module.scss';
import { useEffect, useState } from 'react';
import OptionsDrawerList from './List';
import { motion as m } from 'framer-motion';
import { containerVariants } from '../variants';
import { useClickOutside } from '@starsoft/common/hooks';

/**
 * A component that renders a drawer containing a list of selectable options with search functionality
 *
 * @template T - Type extending FieldValues from react-hook-form
 * @template V - Type of the option value
 *
 * @param {Object} props - Component props
 * @param {Function} props.filterBy - Function to filter options based on search
 * @param {Array<T>} props.options - Array of available options
 * @param {Function} props.setOpen - Function to control drawer visibility
 * @param {string} props.title - Title displayed in drawer header
 * @param {boolean} props.hasNextPage - Whether there are more options to load
 * @param {boolean} props.isContainerColor - Whether to use container background color
 * @param {Function} props.loadMore - Function to load additional options
 * @param {boolean} props.loading - Whether initial options are loading
 * @param {boolean} props.loadingMore - Whether additional options are loading
 * @param {Function} props.getOptionLabel - Function to get display label from option
 * @param {Function} props.getOptionValue - Function to get value from option
 * @param {Function} props.setValue - Function to set selected value
 * @param {Object} props.customComponents - Custom component overrides
 * @param {boolean} props.isAsync - Whether options are loaded asynchronously
 * @param {V} props.value - Currently selected value
 * @param {boolean} props.isSmallerDrawer - Whether to use smaller drawer size
 * @param {boolean} props.useGridOptions - Whether to display options in grid layout
 * @param {boolean} props.hideSearch - Whether to hide search input
 * @param {Function} props.onSearchChange - Callback when search value changes
 * @returns {JSX.Element} Options drawer component
 */
export default function OptionsDrawer<T extends FieldValues, V>({
  filterBy,
  options,
  setOpen,
  title,
  hasNextPage,
  isContainerColor,
  loadMore,
  loading,
  loadingMore,
  getOptionLabel,
  getOptionValue,
  setValue,
  customComponents,
  isAsync,
  value,
  isSmallerDrawer,
  useGridOptions,
  hideSearch,
  onSearchChange,
}: OptionsDrawerProps<T, V>) {
  // State for search input value
  const [search, setSearch] = useState<string>('');

  // Hook to handle clicks outside the drawer
  const ref = useClickOutside({
    open: true,
    onClickOutside: () => setOpen(false),
  });

  /**
   * Updates search state and triggers search change callback
   * @param {string} search - New search value
   */
  function onSetSearch(search: string) {
    setSearch(search);

    if (onSearchChange) {
      onSearchChange(search);
    }
  }

  /**
   * Cleanup function to reset search state on unmount
   */
  function onUnmount() {
    return () => {
      setSearch('');
      if (onSearchChange) {
        onSearchChange('');
      }
    };
  }

  // Reset search on unmount
  useEffect(onUnmount, [onSearchChange]);

  return (
    <m.div
      className={`${styles.container} ${isContainerColor ? styles['container--default'] : ''} ${isSmallerDrawer ? styles['container--smaller'] : ''}`}
      initial="hidden"
      animate="visible"
      exit="exit"
      variants={containerVariants}
      //@ts-ignore
      ref={ref}
    >
      <OptionsDrawerHeader
        search={search}
        setSearch={onSetSearch}
        setOpen={setOpen}
        title={title}
        isContainerColor={isContainerColor}
        loadingMore={loadingMore}
        hideSearch={hideSearch}
        customComponents={customComponents}
      />

      <OptionsDrawerList
        filterBy={filterBy}
        options={options}
        hasNextPage={hasNextPage}
        loadMore={loadMore}
        loading={loading}
        loadingMore={loadingMore}
        useGridOptions={useGridOptions}
        search={search}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        setOpen={setOpen}
        setValue={setValue}
        customComponents={customComponents}
        isAsync={isAsync}
        value={value}
        onSearchChange={onSearchChange}
      />
    </m.div>
  );
}
